import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useActiveClassroom } from '../context/ActiveClassroomProvider.js';
import { loadActive, loadAlarms, saveActive, saveAlarms } from './clockStorage.js';
import type { ActiveState, Alarm, FiringEvent } from './clockTypes.js';
import { playChime, primeAudio } from './chime.js';

/*
 * Provider that owns the clock subsystem's state machine: one active
 * countdown/timer at a time, plus a list of recurring time-of-day
 * alarms. Ticks every 1s, fires takeovers, persists to localStorage
 * keyed by classroom id.
 */

export interface ClockContextValue {
  /** Heartbeat — the ms-since-epoch at the latest tick. Components
   *  read this to derive remaining/elapsed values without holding
   *  their own intervals. */
  now: number;
  active: ActiveState;
  alarms: readonly Alarm[];
  firing: FiringEvent | null;
  startCountdown(name: string, durationMs: number): void;
  startTimer(name: string): void;
  stopActive(): void;
  addAlarm(name: string, hour: number, minute: number): void;
  removeAlarm(id: string): void;
  dismissFiring(): void;
}

const ClockContext = createContext<ClockContextValue | null>(null);

export function useClock(): ClockContextValue {
  const ctx = useContext(ClockContext);
  if (!ctx) throw new Error('useClock must be used inside <ClockProvider>');
  return ctx;
}

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function ClockProvider({ children }: { children: ReactNode }) {
  const { active: classroom } = useActiveClassroom();
  const classroomId = classroom?.id ?? null;

  const [now, setNow] = useState<number>(() => Date.now());
  const [activeState, setActiveState] = useState<ActiveState>({ kind: 'idle' });
  const [alarms, setAlarms] = useState<Alarm[]>([]);
  const [firing, setFiring] = useState<FiringEvent | null>(null);

  // Keep refs up to date so the tick handler (registered once per
  // classroom) sees latest state without re-subscribing each render.
  const activeRef = useRef(activeState);
  const alarmsRef = useRef(alarms);
  const firingRef = useRef(firing);
  useEffect(() => {
    activeRef.current = activeState;
  }, [activeState]);
  useEffect(() => {
    alarmsRef.current = alarms;
  }, [alarms]);
  useEffect(() => {
    firingRef.current = firing;
  }, [firing]);

  // Hydrate state from localStorage when the active classroom changes.
  useEffect(() => {
    if (!classroomId) {
      setActiveState({ kind: 'idle' });
      setAlarms([]);
      setFiring(null);
      return;
    }
    setActiveState(loadActive(classroomId));
    setAlarms(loadAlarms(classroomId));
    setFiring(null);
  }, [classroomId]);

  // Persist active state on change.
  useEffect(() => {
    if (!classroomId) return;
    saveActive(classroomId, activeState);
  }, [classroomId, activeState]);

  // Persist alarms on change.
  useEffect(() => {
    if (!classroomId) return;
    saveAlarms(classroomId, alarms);
  }, [classroomId, alarms]);

  // 1s heartbeat. Drives the clock display, countdown expiry, and the
  // alarm scheduler. Only runs while a classroom is active so we don't
  // tick during hub/detail screens.
  useEffect(() => {
    if (!classroomId) return undefined;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const t = Date.now();
      setNow(t);

      // Countdown expiry → transition to firing + play chime.
      const a = activeRef.current;
      if (a.kind === 'countdown') {
        const ends = a.startedAt + a.durationMs;
        if (t >= ends && firingRef.current === null) {
          setFiring({
            id: makeId(),
            source: 'countdown',
            name: a.name,
            firedAt: ends,
          });
          setActiveState({ kind: 'idle' });
          playChime();
        }
      }

      // Alarm scheduler: any alarm matching current minute that hasn't
      // already fired today fires. We only check once per minute (via
      // the 1s tick) so a single alarm can't fire twice.
      const d = new Date(t);
      const hh = d.getHours();
      const mm = d.getMinutes();
      const today = isoDate(d);
      const due = alarmsRef.current.find(
        (al) => al.hour === hh && al.minute === mm && al.firedDate !== today,
      );
      if (due && firingRef.current === null) {
        setAlarms((prev) =>
          prev.map((al) => (al.id === due.id ? { ...al, firedDate: today } : al)),
        );
        setFiring({
          id: makeId(),
          source: 'alarm',
          name: due.name,
          firedAt: t,
        });
        playChime();
      }
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [classroomId]);

  // ---------- Actions ----------

  const startCountdown = useCallback((name: string, durationMs: number) => {
    primeAudio();
    setActiveState({
      kind: 'countdown',
      name: name.trim() || 'Countdown',
      startedAt: Date.now(),
      durationMs: Math.max(1_000, durationMs),
    });
  }, []);

  const startTimer = useCallback((name: string) => {
    primeAudio();
    setActiveState({
      kind: 'timer',
      name: name.trim() || 'Timer',
      startedAt: Date.now(),
    });
  }, []);

  const stopActive = useCallback(() => {
    setActiveState({ kind: 'idle' });
  }, []);

  const addAlarm = useCallback((name: string, hour: number, minute: number) => {
    primeAudio();
    setAlarms((prev) => [
      ...prev,
      {
        id: makeId(),
        name: name.trim() || 'Alarm',
        hour: Math.max(0, Math.min(23, Math.floor(hour))),
        minute: Math.max(0, Math.min(59, Math.floor(minute))),
      },
    ]);
  }, []);

  const removeAlarm = useCallback((id: string) => {
    setAlarms((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const dismissFiring = useCallback(() => {
    setFiring(null);
  }, []);

  const value = useMemo<ClockContextValue>(
    () => ({
      now,
      active: activeState,
      alarms,
      firing,
      startCountdown,
      startTimer,
      stopActive,
      addAlarm,
      removeAlarm,
      dismissFiring,
    }),
    [
      now,
      activeState,
      alarms,
      firing,
      startCountdown,
      startTimer,
      stopActive,
      addAlarm,
      removeAlarm,
      dismissFiring,
    ],
  );

  return <ClockContext.Provider value={value}>{children}</ClockContext.Provider>;
}
